import type { GroupRef, OwnerRef, PersonRef } from '@ralph/contracts'
import clsx from 'clsx'
import { Search, UserRound } from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import { GroupAvatar } from '@/components/groups/GroupAvatar.tsx'
import { Initials, PersonPicker } from '@/components/people/PersonPicker.tsx'
import { useGroups } from '@/hooks/use-groups.ts'
import { useI18n } from '@/i18n/index.ts'

/**
 * Who something belongs to (PRD-26 D-19, D-32): the person creating or moving it, or a group they
 * manage — and, for an administrator, any group or any person.
 *
 * A radio group rather than a select, because each choice carries a consequence worth reading:
 * a group-owned agent is maintained by that group's managers, and moving something never changes
 * who can use it. The consequence sentence is the caller's (it differs for creating and moving).
 */

/** Shaped as a control, not a link: a touch screen has no hover to reveal an underline. */
const SEARCH_TOGGLE = clsx(
  'inline-flex h-[28px] items-center gap-1.5 rounded-full border px-3 text-[12.5px] tracking-[-0.01em]',
  'transition-[color,background-color,border-color] duration-200',
  'border-rule text-fg-muted hover:border-rule-strong hover:text-fg',
  'aria-pressed:border-primary aria-pressed:bg-card-solid aria-pressed:text-fg'
)

export type OwnerChoice = { kind: 'user'; id: string; name: string } | { kind: 'group'; group: GroupRef }

export const ownerRefOf = (choice: OwnerChoice): OwnerRef =>
  choice.kind === 'user' ? { kind: 'user', id: choice.id } : { kind: 'group', id: choice.group.id }

export const OwnerPicker = ({
  value,
  onChange,
  viewer,
  current,
  allowAnyone = false,
  includeSelf = true,
  disabled = false
}: {
  value: OwnerChoice | null
  onChange: (next: OwnerChoice) => void
  viewer: { id: string; displayName: string; managedGroups: readonly GroupRef[] }
  /** The owner now, for a move: shown as such and not offered again. */
  current?: OwnerRef
  /** Administrators: any group or person (D-32). */
  allowAnyone?: boolean
  includeSelf?: boolean
  disabled?: boolean
}) => {
  const t = useI18n().t.groups.ownerPicker
  const name = useId()
  const isCurrent = (ref: OwnerRef) => current?.kind === ref.kind && current.id === ref.id
  const selected = (ref: OwnerRef) =>
    value !== null && ownerRefOf(value).kind === ref.kind && ownerRefOf(value).id === ref.id

  const [extraGroup, setExtraGroup] = useState<GroupRef | null>(null)
  const [extraPerson, setExtraPerson] = useState<PersonRef[]>([])
  const [mode, setMode] = useState<'list' | 'group' | 'person'>('list')

  const managed = viewer.managedGroups.filter((group) => !isCurrent({ kind: 'group', id: group.id }))
  // The name comes first: on a phone the hint takes the line beneath it, and on a wider screen it
  // wraps within at most 40% of the row, so the name is never the part squeezed.
  const option = (ref: OwnerRef, label: React.ReactNode, hint: string, choice: OwnerChoice, key: string) => (
    <label
      key={key}
      className={clsx(
        'grid cursor-pointer grid-cols-[15px_minmax(0,1fr)] items-center gap-x-2.5 gap-y-0.5 rounded-tile border px-3 py-2',
        'sm:grid-cols-[15px_minmax(0,1fr)_fit-content(40%)]',
        'transition-[background-color,border-color,box-shadow] duration-200',
        selected(ref)
          ? 'border-primary bg-card-solid shadow-glass'
          : 'border-rule bg-card-solid/55 hover:border-rule-strong',
        disabled && 'cursor-not-allowed opacity-45'
      )}
    >
      <input
        type="radio"
        name={name}
        checked={selected(ref)}
        disabled={disabled}
        onChange={() => onChange(choice)}
        className="h-[15px] w-[15px] shrink-0 cursor-pointer accent-primary"
      />
      {label}
      <span className="col-start-2 text-[11.5px] text-fg-subtle sm:col-start-3 sm:row-start-1 sm:text-right">
        {hint}
      </span>
    </label>
  )

  return (
    <fieldset disabled={disabled} className="min-w-0 space-y-2">
      <legend className="mb-2 text-[13.5px] font-semibold tracking-[-0.014em]">{t.legend}</legend>

      {includeSelf &&
        !isCurrent({ kind: 'user', id: viewer.id }) &&
        option(
          { kind: 'user', id: viewer.id },
          <span className="flex min-w-0 items-center gap-2">
            <span
              aria-hidden
              className="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full border border-rule bg-card text-fg-muted"
            >
              <UserRound size={13} />
            </span>
            <span className="truncate text-[13px] font-semibold tracking-[-0.012em]">{t.you(viewer.displayName)}</span>
          </span>,
          t.youHint,
          { kind: 'user', id: viewer.id, name: viewer.displayName },
          'self'
        )}

      {managed.map((group) =>
        option(
          { kind: 'group', id: group.id },
          <span className="flex min-w-0 items-center gap-2">
            <GroupAvatar group={group} size={28} />
            <span className="min-w-0">
              <span className="block truncate text-[13px] font-semibold tracking-[-0.012em]">{group.name}</span>
              <span className="readout block text-fg-subtle">{t.manage}</span>
            </span>
          </span>,
          t.groupHint,
          { kind: 'group', group },
          group.id
        )
      )}

      {allowAnyone &&
        extraGroup &&
        !managed.some((group) => group.id === extraGroup.id) &&
        option(
          { kind: 'group', id: extraGroup.id },
          <span className="flex min-w-0 items-center gap-2">
            <GroupAvatar group={extraGroup} size={28} />
            <span className="truncate text-[13px] font-semibold tracking-[-0.012em]">{extraGroup.name}</span>
          </span>,
          t.groupHint,
          { kind: 'group', group: extraGroup },
          `extra-${extraGroup.id}`
        )}

      {allowAnyone &&
        extraPerson[0] &&
        option(
          { kind: 'user', id: extraPerson[0].id },
          <span className="flex min-w-0 items-center gap-2">
            <Initials person={extraPerson[0]} size={28} />
            <span className="min-w-0">
              <span className="block truncate text-[13px] font-semibold tracking-[-0.012em]">
                {extraPerson[0].displayName}
              </span>
              <span className="block truncate text-[12px] text-fg-muted">{extraPerson[0].email}</span>
            </span>
          </span>,
          t.theyHint,
          { kind: 'user', id: extraPerson[0].id, name: extraPerson[0].displayName },
          `person-${extraPerson[0].id}`
        )}

      {allowAnyone && (
        <div className="flex flex-wrap gap-2 pt-1">
          <button
            type="button"
            aria-pressed={mode === 'group'}
            onClick={() => setMode(mode === 'group' ? 'list' : 'group')}
            className={SEARCH_TOGGLE}
          >
            <Search size={12} aria-hidden />
            {t.anotherGroup}
          </button>
          <button
            type="button"
            aria-pressed={mode === 'person'}
            onClick={() => setMode(mode === 'person' ? 'list' : 'person')}
            className={SEARCH_TOGGLE}
          >
            <Search size={12} aria-hidden />
            {t.anotherPerson}
          </button>
        </div>
      )}

      {allowAnyone && mode === 'group' && (
        <GroupFinder
          exclude={current?.kind === 'group' ? [current.id] : []}
          onPick={(group) => {
            setExtraGroup(group)
            onChange({ kind: 'group', group })
            setMode('list')
          }}
        />
      )}

      {allowAnyone && mode === 'person' && (
        <PersonPicker
          chosen={[]}
          exclude={current?.kind === 'user' ? [current.id] : []}
          autoFocus
          onChange={(people) => {
            const person = people[0]
            if (!person) return
            setExtraPerson([person])
            onChange({ kind: 'user', id: person.id, name: person.displayName })
            setMode('list')
          }}
        />
      )}
    </fieldset>
  )
}

/** A single-choice search over every group, for an administrator's move. */
const GroupFinder = ({ exclude, onPick }: { exclude: readonly string[]; onPick: (group: GroupRef) => void }) => {
  const t = useI18n().t.groups.ownerPicker
  const inputId = useId()
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 180)
    return () => clearTimeout(timer)
  }, [query])
  const groups = useGroups(debounced === '' ? { limit: 20 } : { q: debounced, limit: 20 })
  const items = (groups.data?.items ?? []).filter((group) => !exclude.includes(group.id))

  return (
    <div>
      <div className="relative">
        <label htmlFor={inputId} className="sr-only">
          {t.groupSearch}
        </label>
        <Search size={13} aria-hidden className="pointer-events-none absolute left-3 top-[10px] text-fg-subtle" />
        <input
          id={inputId}
          value={query}
          // biome-ignore lint/a11y/noAutofocus: opened by the button right above it, to be typed into.
          autoFocus
          placeholder={t.groupSearchPlaceholder}
          onChange={(event) => setQuery(event.target.value)}
          className="h-[34px] w-full rounded-tile border border-rule-strong bg-card-solid pl-8 pr-3 text-[13px] text-fg placeholder:text-fg-subtle focus:border-primary focus:outline-none"
        />
      </div>
      <ul className="mt-1.5 max-h-[12rem] space-y-px overflow-y-auto rounded-tile border border-rule bg-card-solid p-1">
        {items.length === 0 && !groups.isLoading && (
          <li className="px-2 py-2 text-[12.5px] text-fg-muted">{t.noGroups}</li>
        )}
        {items.map((group) => (
          <li key={group.id}>
            <button
              type="button"
              onClick={() => onPick(group)}
              className="flex w-full items-center gap-2 rounded-chip px-2 py-1.5 text-left hover:bg-card"
            >
              <GroupAvatar group={group} size={20} />
              <span className="truncate text-[13px] font-medium">{group.name}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
